import { useEffect, useState } from "react";
import { useGetUnreadCountQuery } from "Services/Notifications/Notifications";
import { useMessageCenterSignalR } from "Common/Hooks";
import type { INotification, INotificationResponseDto } from "Common/Interfaces";

const mapSignalRToNotification = (dto: INotificationResponseDto): INotification => ({
  id: String(dto.notificationId),
  applicationId: dto.content?.applicationId,
  title: dto.content?.title ?? "",
  message: dto.content?.message ?? "",
  resources: dto.content?.resources
    ?.filter((r): r is { type: string; value?: string } => Boolean(r.type))
    .map((r) => ({ type: r.type, value: r.value })),
  applicationName: dto.content?.application ?? "",
  isRead: false,
  isDismissed: false,
  createdAt: dto.createdAt,
  deepLink: dto.content?.links?.[0]?.url,
});

export const useNotificationSession = (isEnabled: boolean) => {
  // Stable for the entire app session — reset only on page reload
  const [sessionStartTime] = useState(() => new Date().toISOString());

  // Unread count — synced from API on every change (initial load + after any mutation)
  const [unreadCount, setUnreadCount] = useState(0);
  const { data: unreadCountData } = useGetUnreadCountQuery(undefined, { skip: !isEnabled });
  useEffect(() => {
    if (!isEnabled) {
      setUnreadCount(0);
      return;
    }

    if (unreadCountData !== undefined) {
      setUnreadCount(unreadCountData.count);
    }
  }, [isEnabled, unreadCountData]);

  // Real-time items from SignalR — accumulated until the drawer consumes them
  const [pendingSignalRItems, setPendingSignalRItems] = useState<INotification[]>([]);
  const { startConnection, setNotificationCallback } = useMessageCenterSignalR();

  useEffect(() => {
    if (!isEnabled) return;

    startConnection();
  }, [isEnabled, startConnection]);

  useEffect(() => {
    if (!isEnabled) {
      setPendingSignalRItems([]);
      return;
    }

    setNotificationCallback((notification: INotificationResponseDto) => {
      setPendingSignalRItems((prev) => [mapSignalRToNotification(notification), ...prev]);
      setUnreadCount((prev) => prev + 1);
    });
  }, [isEnabled, setNotificationCallback]);

  const handleNotificationDismissed = () => setUnreadCount((prev) => Math.max(0, prev - 1));

  const handleNotificationMarkedUnread = () => setUnreadCount((prev) => prev + 1);

  const handleAllNotificationsDismissed = () => setUnreadCount(0);

  return {
    sessionStartTime,
    unreadCount,
    pendingSignalRItems,
    setPendingSignalRItems,
    handleNotificationDismissed,
    handleNotificationMarkedUnread,
    handleAllNotificationsDismissed,
  };
};
